import type { RequestParam } from '@core/entities/ApiRequest';

const splitUrl = (url: string): { base: string; query: string } => {
  const index = url.indexOf('?');
  if (index === -1) return { base: url, query: '' };
  return { base: url.slice(0, index), query: url.slice(index + 1) };
};

const parseQueryPairs = (query: string): { key: string; value: string }[] => {
  if (!query) return [];
  return query
    .split('&')
    .filter(Boolean)
    .map((pair) => {
      const eq = pair.indexOf('=');
      if (eq === -1) return { key: decodeQueryComponent(pair), value: '' };
      return {
        key: decodeQueryComponent(pair.slice(0, eq)),
        value: decodeQueryComponent(pair.slice(eq + 1)),
      };
    });
};

const decodeQueryComponent = (value: string): string => {
  try {
    return decodeURIComponent(value.replace(/\+/g, ' '));
  } catch {
    return value;
  }
};

const buildQueryString = (pairs: { key: string; value: string }[]): string =>
  pairs
    .filter((pair) => pair.key.trim() !== '')
    .map((pair) => `${encodeURIComponent(pair.key)}=${encodeURIComponent(pair.value)}`)
    .join('&');

/**
 * Rebuilds the `query`-typed rows in `params` to mirror the URL's own query
 * string as the user types it in the URL bar. Path params and disabled query
 * rows are left untouched (disabled rows don't appear in the URL text, so
 * editing the URL shouldn't discard them).
 */
export const syncParamsFromUrl = (url: string, params: RequestParam[]): RequestParam[] => {
  const pairs = parseQueryPairs(splitUrl(url).query);

  const nonQuery = params.filter((param) => param.type !== 'query');
  const disabledQuery = params.filter((param) => param.type === 'query' && !param.isEnabled);
  const enabledQuery = params.filter((param) => param.type === 'query' && param.isEnabled);

  const usedIds = new Set<string>();
  const nextEnabledQuery: RequestParam[] = pairs.map(({ key, value }) => {
    const existing = enabledQuery.find((param) => param.key === key && !usedIds.has(param.id));
    if (existing) {
      usedIds.add(existing.id);
      return { ...existing, value };
    }
    return {
      id: crypto.randomUUID(),
      type: 'query',
      key,
      value,
      description: '',
      isEnabled: true,
      position: 0,
    };
  });

  return [...nonQuery, ...disabledQuery, ...nextEnabledQuery];
};

/**
 * Rebuilds the URL's query string from the `query`-typed, enabled rows in
 * `params`, as the user edits them in the Params tab.
 */
export const syncUrlFromParams = (url: string, params: RequestParam[]): string => {
  const { base } = splitUrl(url);
  const pairs = params
    .filter((param) => param.type === 'query' && param.isEnabled)
    .map((param) => ({ key: param.key, value: param.value ?? '' }));
  const query = buildQueryString(pairs);
  return query ? `${base}?${query}` : base;
};
